import { Component, Input, OnInit, Output } from '@angular/core';
import { BsModalService } from 'ngx-bootstrap/modal';

import { CacheService } from '../../../global/ng2-cache/services/cache.service';
import { Subject } from 'rxjs/Subject';
import { AlertDialogComponent } from '../../../global/alert-dialog/alert-dialog.component';
import { ConfirmDialogComponent } from '../../../global/confirm-dialog/confirm-dialog.component';
import { Constants, Partner } from '../../../global/constants';
import { AutomateTask, PartnerField, PartnerList, PartnerSiftrockFieldMap, PartnerStaticFieldMap } from '../../../global/models';
import { PartnerMapOptions } from '../../../global/partner-map/partner-map.component';
import { GlobalMessageService } from '../../../services/global-message.service';
import { PartnerService } from '../../../services/partner.service';
import { SessionService } from '../../../services/session2.service';

@Component({
  selector: 'route-task',
  templateUrl: './route-task.component.html'
})

export class RouteTaskComponent implements OnInit {
  @Input() task: AutomateTask;
  @Output() onChange = new Subject<AutomateTask>();
  @Output() onNavChange = new Subject<boolean>();

  session: any;
  showRouteStatic: boolean = false; // need this due to complexity of controls
  routeHoverState: any = {
    static: false,
    dynamic: false,
    leadowner: false
  };

  // Route controls
  staticRoutes: string[] = [];
  newStatic: string = '';
  addingStatic: boolean = false;
  dynamicDomainSet: boolean = false;
  dynamicDomain: string = '';
  selectedPartnerLeadOwner: Partner;
  selectedFieldLeadOwner: PartnerField;
  leadOwnerFallbackAddresses: Array<string> = [];


  // Partner map control
  partnerMapOptions: PartnerMapOptions = {
    listSelector: true,
    staticFieldSelector: true,
    siftrockFieldSelector: true,
    senderFieldSelector: false,
    skillType: 'route'
  };

  selectedPartner: Partner;
  selectedList: PartnerList;
  staticFieldMap: Array<PartnerStaticFieldMap> = [];
  siftrockFieldMap: Array<PartnerSiftrockFieldMap> = [];

  constructor(
    private cacheService: CacheService,
    private sessionService: SessionService,
    private modalService: BsModalService,
    private constants: Constants,
    private globalMessageService: GlobalMessageService,
    private partnerService: PartnerService
  ) { }

  ngOnInit(): void {
    // skill componets assume they are loaded in dynamically via the skill-detail component.
    this.session = this.cacheService.get('session');
    this.initView();
  }

  initView() {
    this.setupRoute();

  }

  /**
 * Change the route task routing type (static, dynamic, lead owner lookup)
 * @param type
 */
  changeRouteType(type: string) {
    if (type == this.task.settings.type) {
      // short-circuit on clicking the same type
      return;
    }

    this.verifyChangeRouteType(this.task, this.task.settings.type, type).then(
      canChange => {
        if (canChange) {
          this.task.settings.type = type;
          // on any change we will remove dynamic and lead owner settings
          this.task.settings.dynamic = '';
          this.task.settings.lead_owner = {};
          this.dynamicDomain = '';
          this.dynamicDomainSet = false;
          this.selectedPartnerLeadOwner = null;
          this.selectedFieldLeadOwner = null;
          this.leadOwnerFallbackAddresses = [];
          this.stage();
        }
      }
    );
  }

  /**
 * Add an email address to the static list of email addresses for routing
 */
  addStaticRoute() {
    if (this.task.settings.static.findIndex(s => { return s.email_address == this.newStatic; }) >= 0) {
      // duplicate - don't allow
      this.modalService.show(AlertDialogComponent, Object.assign({}, {
        initialState: {
          message: `${this.newStatic} is already on your list of specific people.`,
          confirmBtn: 'Ok'
        }
      }));
      this.newStatic = '';
      this.addingStatic = false;
    }

    // not a duplicate - add to list
    this.task.settings.static.push({ email_address: this.newStatic, created_at: new Date() });

    // keep them sorted
    this.task.settings.static.sort((a: any, b: any) => {
      if (a.email_address < b.email_address) {
        return -1;
      }
      else {
        return 1;
      }
    });
    this.staticRoutes = this.task.settings.static;
    this.newStatic = '';
    this.addingStatic = false;
    this.stage();
  }

  removeStaticRoute(emailAddress: string) {
    this.task.settings.static.splice(this.task.settings.static.findIndex(s => { return s.email_address == emailAddress; }), 1);
    this.staticRoutes = this.task.settings.static;
    this.stage();
  }

  setDynamicRoute() {
    this.task.settings.dynamic = this.dynamicDomain;
    this.dynamicDomainSet = true;
    this.stage();
    this.globalMessageService.message('Dynamic route saved');
  }

  removeDynamicRoute() {
    this.dynamicDomain = '';
    this.task.settings.dynamic = this.dynamicDomain;
    this.dynamicDomainSet = false;
    this.stage();
  }

  /**
   * Event from partner-leadowner-picker when the Partner dropdown changes
   * @param partner
   */
  changeLeadOwnerPartner(partner: Partner) {
    this.task.settings.lead_owner.partner = partner.name;
    this.stage();
  }

  /**
   * Event from partner-leadowner-picker when the Field dropdown changes
   * @param field
   */
  changeLeadOwnerField(field: PartnerField) {
    this.task.settings.lead_owner.field = field;
    this.stage();
  }

  /**
   * Event from partner-leadowner-picker when the fallback addresses change
   * @param addresses
   */
  changeLeadOwnerFallback(addresses: Array<string>) {
    this.task.settings.lead_owner.fallback = addresses;
    this.stage();
  }

  step(next: boolean) {
    this.onNavChange.next(next);
  }

  private setupRoute() {
    this.staticRoutes = this.task.settings.static;
    this.dynamicDomain = this.task.settings.dynamic;
    if (this.dynamicDomain.length > 0) {
      this.dynamicDomainSet = true;
    }

    // lead_owner configuration
    if (this.task.settings.lead_owner.partner) {
      this.selectedPartnerLeadOwner = this.constants.partners.find(p => { return p.name == this.task.settings.lead_owner.partner; });
      if (this.task.settings.lead_owner.field) {
        this.selectedFieldLeadOwner = this.task.settings.lead_owner.field;
      }
      if (this.task.settings.lead_owner.fallback) {
        this.leadOwnerFallbackAddresses = this.task.settings.lead_owner.fallback;
      }
    }

    // some state management happens here
    this.checkValidRoute();
  }

  /**
 * Check validity of route task and set meta data appropriately
 */
  private checkValidRoute() {
    let valid: boolean = true;
    let invalidReason: string = null;
    if (this.task) {
      if (!this.task.settings.type || this.task.settings.type.length == 0) {
        valid = false;
        invalidReason = 'A route type is required for routing';
      }
      else {
        if (this.task.settings.type == 'static' && this.task.settings.static.length == 0) {
          valid = false;
          invalidReason = 'At least one email address is required for routing';
        }
        else if (this.task.settings.type == 'dynamic' && this.task.settings.dynamic.length == 0) {
          valid = false;
          invalidReason = 'A domain is required for dynamic routing';
        }
        else if (this.task.settings.type == 'leadowner') {
          if (!this.task.settings.lead_owner.partner || this.task.settings.lead_owner.partner.length == 0) {
            valid = false;
            invalidReason = 'An integration must be selected for lead owner lookup routing';
          }
          else if (!this.task.settings.lead_owner.field || !this.task.settings.lead_owner.field.id) {
            valid = false;
            invalidReason = 'Lead owner lookup routing requires a field';
          }
          else if (!this.task.settings.lead_owner.fallback || this.task.settings.lead_owner.fallback.length == 0) {
            valid = false;
            invalidReason = 'At least one fallback address is required for lead owner lookup routing';
          }
        }

        // set static route state
        this.showRouteStatic = false;
        if (this.task.settings.type == 'static' || this.task.settings.type == 'dynamic' || this.task.settings.type == 'leadowner') {
          this.showRouteStatic = true;
        }
        if (this.task.settings.type == 'leadowner' && !this.session.f.leadowner) {
          this.showRouteStatic = false;
        }
      }
    }

    this.task.valid = valid;
    this.task.invalid_reason = invalidReason;
  }


  private verifyChangeRouteType(routeTask: AutomateTask, oldType: string, newType: string): Promise<boolean> {
    let needDialog: boolean = false;
    let dialogMessage: string = '';

    if (oldType == 'dynamic' && routeTask.settings.dynamic.length > 0) {
      needDialog = true;
      dialogMessage = 'WARNING: you have a dynamic route set. If you change the route type we will clear the dynamic route configuration. Please confirm you\'d like to do this.';
    }

    if (oldType == 'leadowner' && routeTask.settings.lead_owner.partner) {
      needDialog = true;
      dialogMessage = 'WARNING: you have lead owner lookup routing set. If you change the route type we will clear the lead owner lookup configuration. Please confirm you\'d like to do this.';
    }

    if (needDialog) {
      let me = this;
      return new Promise((resolve, reject) => {
        this.modalService.show(ConfirmDialogComponent, Object.assign({}, {
          initialState: {
            message: dialogMessage,
            yesBtn: 'Confirm',
            confirm: function() {
              // confirmed
              resolve(true);
            }
          }
        }));
      });
    }
    else {
      return Promise.resolve(true);
    }
  }

/**
 * Stage changes to be saved
 */
  private stage() {
    this.checkValidRoute();
    this.task.updated_at = new Date();
    this.onChange.next(this.task);
  }

}



// WEBPACK FOOTER //
// ./client/app/automate/tasks/route-task/route-task.component.ts